import dayjs from "dayjs";
import { Stack, Typography } from "@altimateai/lego";
import { formatNumber } from "@uicore";
import type { TooltipProps } from "recharts";
import { useAppState } from "@/modules/app/useAppContext";
import type { Granularity } from "./ClusterCostTrends";

interface Props extends TooltipProps<number, string> {
  granularity: Granularity;
}

const formatPeriod = (period: string, granularity: Granularity): string => {
  if (granularity === "month") return dayjs(period).format("MMM YYYY");
  if (granularity === "week") return `Week of ${dayjs(period).format("DD MMM YYYY")}`;
  return dayjs(period).format("DD MMM YYYY");
};

const ClusterCostTooltip = ({ active, payload, granularity = "day" }: Props) => {
  const { currency } = useAppState();

  if (!active || !payload?.length) return null;

  const period = payload[0]?.payload?.period;
  if (typeof period !== "string") return null;

  // Recharts orders payload bottom-to-top in the stack; reverse so the
  // visually-topmost segment is listed first in the tooltip.
  const rows = [...payload].reverse().filter(entry => Number(entry.value) > 0);
  const total = payload.reduce((sum, entry) => sum + (Number(entry.value) || 0), 0);

  return (
    <div className="al-min-w-[220px] al-rounded-lg al-border al-bg-background al-px-4 al-py-3 al-shadow-lg">
      <Stack
        direction="row"
        align="center"
        justify="between"
        className="al-mb-2 al-border-b al-pb-2"
      >
        <Typography size="sm" className="al-font-medium al-text-foreground">
          {formatPeriod(period, granularity)}
        </Typography>
        <Typography size="sm" className="al-font-semibold al-text-foreground">
          {formatNumber(total, { currency, maximumFractionDigits: 2 })}
        </Typography>
      </Stack>
      <Stack direction="column" gap="xs">
        {rows.map(entry => (
          <Stack
            key={String(entry.dataKey)}
            direction="row"
            align="center"
            justify="between"
            gap="md"
          >
            <Stack direction="row" align="center" gap="xs" className="al-min-w-0">
              <span
                aria-hidden="true"
                className="al-inline-block al-h-2 al-w-2 al-shrink-0 al-rounded-sm"
                style={{ background: entry.color }}
              />
              <Typography size="xs" className="al-truncate al-text-muted-foreground">
                {entry.name}
              </Typography>
            </Stack>
            <Typography size="xs" className="al-font-medium al-text-foreground">
              {formatNumber(Number(entry.value), { currency, maximumFractionDigits: 2 })}
            </Typography>
          </Stack>
        ))}
      </Stack>
    </div>
  );
};

export default ClusterCostTooltip;
